import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'
import { Logo } from '@/components/ui/Logo'
import { LoadingScreen } from '@/components/ui/LoadingScreen'
import {
  savePendingInstructorInviteToken,
  readPendingInstructorInviteToken,
  clearPendingInstructorInviteToken,
} from '@/lib/pendingInstructorInvite'

const ERROR_MESSAGES: Record<string, string> = {
  not_found:            'This invite link is invalid or has already been used.',
  missing_token:        'This invite link is invalid.',
  expired:               'This invitation has expired. Ask the school to send a new one.',
  revoked:                'This invitation has been revoked by the school.',
  already_used:           'This invitation was already accepted by a different account.',
  email_mismatch:         'This invitation was sent to a different email address. Sign in with the account that received it.',
  email_not_verified:     'Please verify your email address before accepting this invitation.',
  wrong_role:             'This invitation is for an instructor account. Sign in with an instructor account, or create one, to accept it.',
  account_not_active:     'Your account is not currently active. Contact support for help.',
  already_affiliated:     'Your account is already associated with a school.',
  rejoin_not_supported:   'This account has a prior history with this school that prevents rejoining automatically. Contact the school directly.',
  internal_error:         'Something went wrong. Please try again.',
}

// Codes where the problem is "signed in as the wrong account" — retryable simply by
// signing out and signing back in as the invited email, so the token must survive the
// failed attempt rather than being discarded as if the invitation itself were spent.
const RETRYABLE_BY_SWITCHING_ACCOUNT = new Set(['email_mismatch', 'wrong_role'])

type Outcome =
  | { kind: 'pending' }
  | { kind: 'success' }
  | { kind: 'error'; message: string; code?: string }
  | { kind: 'invalid_link' }

export default function AcceptInstructorInvite() {
  const navigate = useNavigate()
  const { user, profile, loading: authLoading, refreshProfile } = useAuth()

  const [token, setToken] = useState<string | null>(null)
  const [outcome, setOutcome] = useState<Outcome>({ kind: 'pending' })
  const attempted = useRef(false)

  // Capture the token once, from the URL fragment (never a query param — a fragment is
  // never sent to the server in the initial request and is not logged by typical server
  // access logs). Immediately strip it from the visible URL/browser history, and keep it
  // in localStorage (never sessionStorage — see pendingInstructorInvite.ts) so it survives
  // a redirect through /login or /register, INCLUDING the confirmation-email link opening
  // in a brand new browser tab, for an invitee who isn't signed in yet.
  useEffect(() => {
    const hash = new URLSearchParams(window.location.hash.replace(/^#/, ''))
    const fromUrl = hash.get('token')

    if (fromUrl) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search)
      savePendingInstructorInviteToken(fromUrl)
      setToken(fromUrl)
      return
    }

    const fromStorage = readPendingInstructorInviteToken()
    if (fromStorage) {
      setToken(fromStorage)
    } else {
      setOutcome({ kind: 'invalid_link' })
    }
  }, [])

  useEffect(() => {
    if (!token || authLoading || !user || attempted.current) return
    attempted.current = true

    supabase.rpc('accept_instructor_invitation', { p_raw_token: token }).then(({ data, error }) => {
      if (error) {
        clearPendingInstructorInviteToken()
        setOutcome({ kind: 'error', message: ERROR_MESSAGES.internal_error })
        return
      }
      const result = data as { ok: boolean; code?: string } | null
      if (result?.ok) {
        clearPendingInstructorInviteToken()
        setOutcome({ kind: 'success' })
        // accept_instructor_invitation() just wrote full_name/phone/instructor_profiles
        // (licence, vehicle, address, photo) onto this account server-side. AuthContext's
        // profile/instructorProfile were fetched at sign-in, BEFORE this RPC ran, so without
        // this refresh the upcoming redirect to /app/instructor/onboarding would read stale,
        // still-empty data and fail to pre-fill the "Complete your profile" form with what
        // the school already entered.
        refreshProfile().finally(() => {
          setTimeout(() => navigate('/app/instructor', { replace: true }), 1500)
        })
      } else {
        const code = result?.code ?? ''
        // Only discard the token once the invitation itself is actually spent (used,
        // revoked, expired, not found). A wrong-account mismatch is retryable — the token
        // must still be there once the user signs back in as the right account.
        if (!RETRYABLE_BY_SWITCHING_ACCOUNT.has(code)) {
          clearPendingInstructorInviteToken()
        } else {
          attempted.current = false
        }
        setOutcome({ kind: 'error', message: ERROR_MESSAGES[code] ?? ERROR_MESSAGES.internal_error, code })
      }
    })
  }, [token, authLoading, user, navigate])

  if (authLoading) return <LoadingScreen />

  const Shell = ({ children }: { children: React.ReactNode }) => (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center',
      justifyContent: 'center', padding: '24px 16px', background: '#FAF6EF',
    }}>
      <div style={{ width: '100%', maxWidth: 440 }}>
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 22 }}>
          <Logo size={60} />
        </div>
        <div style={{
          background: '#fff', border: '1px solid rgba(27,42,74,0.08)',
          borderRadius: 18, padding: '28px 24px', textAlign: 'center',
        }}>
          {children}
        </div>
      </div>
    </div>
  )

  if (outcome.kind === 'invalid_link') {
    return (
      <Shell>
        <div style={{ font: '600 20px Lora,serif', color: '#1B2A4A', marginBottom: 10 }}>Invalid invitation link</div>
        <div style={{ font: '400 14px/1.6 Inter,sans-serif', color: 'rgba(27,42,74,0.6)' }}>
          This link is missing its invitation code. Please use the link from your invitation email.
        </div>
      </Shell>
    )
  }

  if (!user) {
    return (
      <Shell>
        <div style={{ font: '600 20px Lora,serif', color: '#1B2A4A', marginBottom: 10 }}>
          You've been invited to teach on DriveTrack
        </div>
        <div style={{ font: '400 14px/1.6 Inter,sans-serif', color: 'rgba(27,42,74,0.6)', marginBottom: 22 }}>
          Sign in or create an instructor account with the email this invitation was sent to, and you'll come right back here to accept.
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <button className='btn-primary' onClick={() => navigate('/login?redirect=/accept-instructor-invite')}>
            Sign in
          </button>
          <button className='btn-teal' onClick={() => navigate('/register?role=instructor')}>
            Create an instructor account
          </button>
        </div>
      </Shell>
    )
  }

  if (outcome.kind === 'success') {
    return (
      <Shell>
        <div style={{
          width: 56, height: 56, borderRadius: '50%',
          background: 'rgba(15,124,108,0.12)', border: '2px solid #0F7C6C',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 18px',
        }}>
          <svg width='24' height='24' viewBox='0 0 24 24' fill='none'>
            <path d='M5 13l4 4L19 7' stroke='#0F7C6C' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round'/>
          </svg>
        </div>
        <div style={{ font: '600 20px Lora,serif', color: '#1B2A4A', marginBottom: 8 }}>You're in!</div>
        <div style={{ font: '400 14px/1.6 Inter,sans-serif', color: 'rgba(27,42,74,0.6)' }}>
          Taking you to your instructor dashboard…
        </div>
      </Shell>
    )
  }

  if (outcome.kind === 'error') {
    const canSwitchAccount = !!outcome.code && RETRYABLE_BY_SWITCHING_ACCOUNT.has(outcome.code)
    return (
      <Shell>
        <div style={{ font: '600 20px Lora,serif', color: '#1B2A4A', marginBottom: 10 }}>Couldn't accept this invitation</div>
        <div className='banner-error' style={{ textAlign: 'left', marginBottom: 18 }}>{outcome.message}</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {canSwitchAccount && (
            <button
              className='btn-primary'
              onClick={async () => {
                await supabase.auth.signOut()
                navigate('/login?redirect=/accept-instructor-invite')
              }}
            >
              Sign out and sign in with a different account
            </button>
          )}
          <button
            className={canSwitchAccount ? 'btn-teal' : 'btn-primary'}
            onClick={() => navigate(profile?.primary_role ? `/app/${profile.primary_role === 'school_admin' ? 'school' : profile.primary_role}` : '/')}
          >
            Continue to DriveTrack
          </button>
        </div>
      </Shell>
    )
  }

  return <LoadingScreen />
}
